import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp, Users, DollarSign,
  ChevronRight, BarChart3, PieChart, FileSpreadsheet,
  Percent, ArrowLeft, Landmark
} from 'lucide-react';
import clsx from 'clsx';

// Importar sub-reportes
import ReportesEspeciales from './ReportesEspeciales';
import VentasPeriodo from './VentasPeriodo';
import VentasCliente from './VentasCliente';
import CuentasCobrar from './CuentasCobrar';
import DiferencialCambiario from './DiferencialCambiario';
import RetencionesRenta from './RetencionesRenta';
import VentasProducto from './VentasProducto';
import IvaVentasDebito from './IvaVentasDebito';

// Definición de reportes disponibles
const REPORTES = [
  {
    id: 'impuesto-911',
    categoria: 'impuestos',
    nombre: 'Impuesto 911',
    descripcion: 'Reporte de impuesto Ley 7972 para declaración ante el Sistema de Emergencias 911',
    icon: Percent,
    color: 'red',
    disponible: true,
    component: ReportesEspeciales,
    props: { tipoImpuesto: '07', titulo: 'Impuesto 911' }
  },
  {
    id: 'impuesto-cruz-roja',
    categoria: 'impuestos',
    nombre: 'Impuesto Cruz Roja',
    descripcion: 'Reporte de impuesto Ley 7972 para declaración ante Cruz Roja Costarricense',
    icon: Percent,
    color: 'rose',
    disponible: true,
    component: ReportesEspeciales,
    props: { tipoImpuesto: '99', titulo: 'Impuesto Cruz Roja' }
  },
  {
    id: 'ventas-periodo',
    categoria: 'ventas',
    nombre: 'Ventas por Período',
    descripcion: 'Resumen de ventas agrupadas por día, semana o mes con totales y comparativos',
    icon: TrendingUp,
    color: 'blue',
    disponible: true,
    component: VentasPeriodo
  },
  {
    id: 'ventas-cliente',
    categoria: 'ventas',
    nombre: 'Ventas por Cliente',
    descripcion: 'Análisis de ventas desglosado por cliente con ranking y tendencias',
    icon: Users,
    color: 'indigo',
    disponible: true,
    component: VentasCliente
  },
  {
    id: 'ventas-producto',
    categoria: 'ventas',
    nombre: 'Ventas por Producto',
    descripcion: 'Productos más vendidos con detalle por cliente y % del total',
    icon: BarChart3,
    color: 'purple',
    disponible: true,
    component: VentasProducto
  },
  {
    id: 'iva-ventas',
    categoria: 'impuestos',
    nombre: 'IVA Ventas (Débito)',
    descripcion: 'Detalle de IVA cobrado en ventas para declaración D-104',
    icon: FileSpreadsheet,
    color: 'emerald',
    disponible: true,
    component: IvaVentasDebito
  },
  {
    id: 'cuentas-cobrar',
    categoria: 'cxc',
    nombre: 'Cuentas por Cobrar',
    descripcion: 'Antigüedad de saldos y estado de cartera por cliente',
    icon: DollarSign,
    color: 'amber',
    disponible: true,
    component: CuentasCobrar
  },
  {
    id: 'diferencial-cambiario',
    categoria: 'contabilidad',
    nombre: 'Diferencial Cambiario',
    descripcion: 'Ganancias y pérdidas por tipo de cambio en cobros y pagos en moneda extranjera',
    icon: TrendingUp,
    color: 'cyan',
    disponible: true,
    component: DiferencialCambiario
  },
  {
    id: 'retenciones-renta',
    categoria: 'impuestos',
    nombre: 'Retenciones 2% Renta',
    descripcion: 'Saldo a favor acumulado por retenciones del 2% de renta de entidades del Estado, por cliente y factura',
    icon: Landmark,
    color: 'purple',
    disponible: true,
    component: RetencionesRenta
  },
];

const CATEGORIAS = [
  { id: 'todos', nombre: 'Todos', icon: BarChart3 },
  { id: 'ventas', nombre: 'Ventas', icon: TrendingUp },
  { id: 'impuestos', nombre: 'Impuestos', icon: Percent },
  { id: 'cxc', nombre: 'Cuentas x Cobrar', icon: DollarSign },
  { id: 'contabilidad', nombre: 'Contabilidad', icon: FileSpreadsheet },
];

const COLOR_CLASSES = {
  red: {
    bg: 'bg-red-50',
    icon: 'bg-red-100 text-red-600',
    border: 'border-red-200 hover:border-red-300',
    badge: 'bg-red-100 text-red-700'
  },
  blue: {
    bg: 'bg-blue-50',
    icon: 'bg-blue-100 text-blue-600',
    border: 'border-blue-200 hover:border-blue-300',
    badge: 'bg-blue-100 text-blue-700'
  },
  indigo: {
    bg: 'bg-indigo-50',
    icon: 'bg-indigo-100 text-indigo-600',
    border: 'border-indigo-200 hover:border-indigo-300',
    badge: 'bg-indigo-100 text-indigo-700'
  },
  purple: {
    bg: 'bg-purple-50',
    icon: 'bg-purple-100 text-purple-600',
    border: 'border-purple-200 hover:border-purple-300',
    badge: 'bg-purple-100 text-purple-700'
  },
  emerald: {
    bg: 'bg-emerald-50',
    icon: 'bg-emerald-100 text-emerald-600',
    border: 'border-emerald-200 hover:border-emerald-300',
    badge: 'bg-emerald-100 text-emerald-700'
  },
  slate: {
    bg: 'bg-slate-50',
    icon: 'bg-slate-100 text-slate-600',
    border: 'border-slate-200 hover:border-slate-300',
    badge: 'bg-slate-100 text-slate-700'
  },
  cyan: {
    bg: 'bg-cyan-50',
    icon: 'bg-cyan-100 text-cyan-600',
    border: 'border-cyan-200 hover:border-cyan-300',
    badge: 'bg-cyan-100 text-cyan-700'
  },
  amber: {
    bg: 'bg-amber-50',
    icon: 'bg-amber-100 text-amber-600',
    border: 'border-amber-200 hover:border-amber-300',
    badge: 'bg-amber-100 text-amber-700'
  },
  rose: {
    bg: 'bg-rose-50',
    icon: 'bg-rose-100 text-rose-600',
    border: 'border-rose-200 hover:border-rose-300',
    badge: 'bg-rose-100 text-rose-700'
  },
};

export default function Reportes() {
  const [categoriaActiva, setCategoriaActiva] = useState('todos');
  const [reporteSeleccionado, setReporteSeleccionado] = useState(null);

  // Filtrar reportes por categoría
  const reportesFiltrados = categoriaActiva === 'todos'
    ? REPORTES
    : REPORTES.filter(r => r.categoria === categoriaActiva);

  // Si hay un reporte seleccionado, mostrar su componente
  if (reporteSeleccionado) {
    const ReporteComponent = reporteSeleccionado.component;
    const reporteProps = reporteSeleccionado.props || {};

    return (
      <div className="space-y-4">
        {/* Botón volver */}
        <motion.button
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => setReporteSeleccionado(null)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors group"
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
          <span>Volver a Reportes</span>
        </motion.button>

        {/* Componente del reporte */}
        <ReporteComponent {...reporteProps} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filtros por categoría */}
      <div className="flex flex-wrap gap-2">
        {CATEGORIAS.map(cat => {
          const isActive = categoriaActiva === cat.id;
          const Icon = cat.icon;

          return (
            <button
              key={cat.id}
              onClick={() => setCategoriaActiva(cat.id)}
              className={clsx(
                'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
                isActive
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              )}
            >
              <Icon className="h-4 w-4" />
              {cat.nombre}
            </button>
          );
        })}
      </div>

      {/* Grid de reportes - compacto */}
      <motion.div
        layout
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
      >
        <AnimatePresence mode="popLayout">
          {reportesFiltrados.map((reporte, index) => {
            const colors = COLOR_CLASSES[reporte.color];
            const Icon = reporte.icon;

            return (
              <motion.div
                key={reporte.id}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: index * 0.03 }}
                onClick={() => reporte.disponible && setReporteSeleccionado(reporte)}
                className={clsx(
                  'relative bg-white rounded-lg border p-3 transition-all group',
                  reporte.disponible
                    ? `${colors.border} cursor-pointer hover:shadow-sm`
                    : 'border-gray-100 opacity-50 cursor-not-allowed'
                )}
              >
                {/* Badge de próximamente */}
                {!reporte.disponible && (
                  <span className="absolute top-2 right-2 text-[9px] font-medium text-gray-400">
                    Próximamente
                  </span>
                )}

                {/* Header: Icono + Título en línea */}
                <div className="flex items-center gap-2 mb-1.5">
                  <div className={clsx(
                    'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                    reporte.disponible ? colors.icon : 'bg-gray-100 text-gray-400'
                  )}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 leading-tight">
                    {reporte.nombre}
                  </h3>
                </div>

                {/* Descripción */}
                <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 ml-10">
                  {reporte.descripcion}
                </p>

                {/* Flecha de acción */}
                {reporte.disponible && (
                  <ChevronRight className="absolute right-2 bottom-3 h-4 w-4 text-gray-300 group-hover:text-gray-500 group-hover:translate-x-0.5 transition-all" />
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </motion.div>

      {/* Mensaje si no hay reportes */}
      {reportesFiltrados.length === 0 && (
        <div className="text-center py-12">
          <PieChart className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No hay reportes en esta categoría
          </h3>
          <p className="text-gray-500">
            Selecciona otra categoría o espera nuevos reportes
          </p>
        </div>
      )}

    </div>
  );
}
